import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle, WarningCircle, CircleNotch } from "@phosphor-icons/react";

export default function VerifyEmail() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const token = sp.get("token");
  const [status, setStatus] = useState("verifying"); // verifying | success | error | resent
  const [message, setMessage] = useState("Confirming your email…");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("This link is missing its confirmation token. Please use the link from your email.");
      return;
    }
    (async () => {
      try {
        const { data } = await http.post("/auth/email/verify", { token });
        setStatus("success");
        setMessage(data.message || "Email confirmed. Welcome to Mayoto.");
        if (user) { await refresh(); }
      } catch (e) {
        setStatus("error");
        setMessage(e?.response?.data?.detail || "This verification link is invalid or has expired.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const resend = async () => {
    try {
      await http.post("/auth/email/resend");
      setStatus("resent");
      setMessage("A fresh verification link has been sent to your email.");
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Could not send a new verification email.");
    }
  };

  const Icon = status === "success" || status === "resent" ? CheckCircle
    : status === "error" ? WarningCircle
    : CircleNotch;
  const iconColor = status === "success" || status === "resent" ? "text-emerald-500"
    : status === "error" ? "text-amber-500"
    : "text-[#00b4d8] animate-spin";

  return (
    <div className="min-h-[70vh] grid place-items-center bg-[#f8fafc] py-16">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-3xl p-10 text-center shadow-sm">
        <Icon size={56} className={`mx-auto ${iconColor}`} weight={status === "verifying" ? "bold" : "fill"} />
        <h1 className="mt-5 font-display font-black text-2xl text-[#0a1128]" data-testid="verify-email-title">
          {status === "success" ? "Email confirmed" : status === "error" ? "Something went wrong" : status === "resent" ? "Check your inbox" : "Verifying your email"}
        </h1>
        <p className="mt-3 text-slate-600 text-sm leading-relaxed">{message}</p>

        {status === "success" && (
          <Button onClick={() => nav(user ? "/dashboard" : "/login")} className="mt-6 bg-[#00b4d8] hover:bg-[#0096c7] rounded-full px-6" data-testid="verify-email-cta">
            {user ? "Go to Dashboard" : "Sign in"}
          </Button>
        )}
        {status === "error" && user && (
          <Button onClick={resend} className="mt-6 bg-[#0a1128] hover:bg-[#071026] text-white rounded-full px-6" data-testid="verify-email-resend">
            Send me a new link
          </Button>
        )}
        {status === "error" && !user && (
          <Link to="/login"><Button className="mt-6 bg-[#0a1128] hover:bg-[#071026] text-white rounded-full px-6">Back to sign in</Button></Link>
        )}
      </div>
    </div>
  );
}
